/**
 * Onthoudt per kamer welke taal en stem je koos, zodat terugkomen één tik kost.
 *
 * WAAROM DIT ER IS. Op een telefoon gooit de browser de pagina weg zodra je een
 * tijdje naar een andere app kijkt. Bij terugkomst laadt hij opnieuw, en dan is
 * de schermstatus "ik was al binnen" verdwenen: je staat weer op het
 * voorportaal en kiest opnieuw je taal.
 *
 * HET GESPREK ZELF RAAK JE NIET KWIJT. De server bewaart de laatste 200
 * berichten per kamer en stuurt ze mee zodra je opnieuw binnenkomt. Dit gaat
 * dus alleen over de drie tikken ervoor.
 *
 * WAAROM NIET AUTOMATISCH NAAR BINNEN. De knop op het voorportaal vraagt in
 * DEZELFDE klik de microfoontoestemming én ontgrendelt het geluid. Een browser
 * staat dat alleen toe bij een echte aanraking; een useEffect telt niet. Zou
 * deze module je vanzelf naar binnen zetten, dan zat je in het gesprek met het
 * geluid op slot - je ziet berichten en hoort niets, zonder melding. Eén tik is
 * daarom beter dan nul.
 *
 * SESSIONSTORAGE EN GEEN LOCALSTORAGE, en dat is met opzet: dit hoort bij dit
 * tabblad en deze sessie, net als het sessietoken van de websocket. Sluit je
 * het tabblad, dan is het weg - en dat is precies wat je wilt bij een gesprek
 * dat je op een geleende telefoon voerde.
 *
 * NOOIT DE PINCODE. Die wordt bewust niet bewaard. Staat er een pincode op de
 * kamer, dan hervat deze module niets en typ je hem gewoon opnieuw.
 */

export interface Hervatting {
  taal: string;
  gender: 'man' | 'vrouw';
}

function sleutel(kamerId: string): string {
  return `tolk.hervat.${kamerId}`;
}

/**
 * Wat je de vorige keer koos, of null.
 *
 * De aanroeper geeft de twee talen van de KAMER mee. Staat de bewaarde taal
 * daar niet bij, dan geven we null terug: de kamer kan van taal gewisseld zijn,
 * en dan is een oude keuze geen keuze meer maar een fout.
 */
export function leesHervatting(kamerId: string, talen: readonly string[]): Hervatting | null {
  try {
    const ruw = sessionStorage.getItem(sleutel(kamerId));
    if (ruw === null) return null;
    const uit: unknown = JSON.parse(ruw);
    if (typeof uit !== 'object' || uit === null) return null;
    const { taal, gender } = uit as Record<string, unknown>;
    if (typeof taal !== 'string' || !talen.includes(taal)) return null;
    if (gender !== 'man' && gender !== 'vrouw') return null;
    return { taal, gender };
  } catch {
    // Geen opslag (privévenster), of onleesbare inhoud. Beide betekenen
    // hetzelfde: we weten het niet, dus we hervatten niets.
    return null;
  }
}

export function onthoudHervatting(kamerId: string, hervatting: Hervatting): void {
  try {
    sessionStorage.setItem(sleutel(kamerId), JSON.stringify(hervatting));
  } catch {
    // Niet kunnen onthouden mag het binnengaan nooit tegenhouden.
  }
}

export function vergeetHervatting(kamerId: string): void {
  try {
    sessionStorage.removeItem(sleutel(kamerId));
  } catch {
    // Zie boven.
  }
}
